import { Session } from '../types/session';
import { Card, CardContent } from './ui';
import { formatConferenceTime, formatConferenceDate } from '../lib/timezone';

interface SessionCardProps {
  session: Session;
  onClick?: () => void;
}

export default function SessionCard({ session, onClick }: SessionCardProps) {
  const formatTime = (dateString: string) => formatConferenceTime(dateString);

  const formatDate = (dateString: string) => formatConferenceDate(dateString);

  return (
    <Card 
      className="hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-usc-garnet mb-1">
              {session.title}
            </h3>
            <p className="text-sm text-usc-gray-600">
              {formatDate(session.startTime)}
            </p>
          </div>
          <div className="text-sm text-usc-gray-700 sm:text-right">
            <div className="font-medium">
              {formatTime(session.startTime)} - {formatTime(session.endTime)}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm text-usc-gray-700">
          <svg
            className="w-5 h-5 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <div>
            <div className="font-medium">{session.room.name}</div>
            <div className="text-usc-gray-600">{session.room.building}</div>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm text-usc-gray-700">
          <svg
            className="w-5 h-5 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <div>
            <div className="font-medium">{session.speakerName}</div>
            {session.speakerAffiliation && (
              <div className="text-usc-gray-600">{session.speakerAffiliation}</div>
            )}
          </div>
        </div>

        <p className="text-sm text-usc-gray-700 line-clamp-3">
          {session.description}
        </p>

        {session.track && (
          <div className="pt-2">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-usc-gray-100 text-usc-gray-700 rounded-full">
              {session.track}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
